import { formatExamDateModal, useExamEventDetails } from '@exam-center/exam-event-management';
import {
  GetExamEventExamineeCountSummaryDocument,
  useGetExamEventExamineeByIdLazyQuery,
  useReinstateExamEventExamineeMutation,
} from '@exam-center/graphql';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Flex,
  Form,
  FormTextarea,
  Text,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import {
  type ReinstateExamEventExamineeFormInput,
  ReinstateExamEventExamineeFormSchema,
} from './schema';

interface ReinstateExamEventExamineeModalProps {
  open: boolean;
  onClose: () => void;
  examEventExamineeId: string;
  examCourseCode: string;
}

export default function ReinstateExamEventExamineeModal(
  props: Readonly<ReinstateExamEventExamineeModalProps>,
) {
  const { open: isOpen, onClose, examEventExamineeId, examCourseCode } = props;

  const {
    examEvent: { examStartedAt, examEndedAt },
  } = useExamEventDetails();

  const { showSnackbar } = useSnackbar();

  const formContext = useForm<ReinstateExamEventExamineeFormInput>({
    resolver: zodResolver(ReinstateExamEventExamineeFormSchema),
    defaultValues: { reason: '' },
  });

  const { handleSubmit, reset, formState } = formContext;

  const [getExaminee, { data: examineeData }] = useGetExamEventExamineeByIdLazyQuery();

  useEffect(() => {
    if (isOpen) {
      getExaminee({
        variables: { getExamEventExamineeByIdInput: { id: examEventExamineeId } },
      });
    }
  }, [isOpen, examEventExamineeId, getExaminee]);

  const examinee = examineeData?.ecGetExamEventExamineeById?.examEventExaminee;
  const fullName = examinee
    ? [
        examinee.examinee.title,
        examinee.examinee.firstName,
        examinee.examinee.middleName,
        examinee.examinee.lastName,
      ]
        .filter(Boolean)
        .join(' ')
    : '-';
  const idNumber = examinee?.examinee.idNumber ?? '-';

  const [reinstateExaminee, { loading }] = useReinstateExamEventExamineeMutation({
    refetchQueries: [GetExamEventExamineeCountSummaryDocument],
    awaitRefetchQueries: true,
  });

  const handleReinstate = async (data: ReinstateExamEventExamineeFormInput) => {
    await reinstateExaminee({
      variables: {
        reinstateExamEventExamineeInput: {
          examEventExamineeId,
          remark: data.reason,
        },
      },
      onCompleted: () => {
        showSnackbar({ message: 'คืนสถานะผู้เข้าสอบเรียบร้อยแล้ว', variant: 'success' });
        onClose();
      },
      onError: () => {
        showSnackbar({
          message: 'คืนสถานะผู้เข้าสอบไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
          variant: 'error',
        });
      },
    });
  };

  return (
    <FormProvider {...formContext}>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth='sm'
        fullWidth
        disableEscapeKeyDown={formState.isSubmitting}
        slotProps={{
          paper: {
            component: Form,
            onSubmit: handleSubmit(handleReinstate),
          },
          transition: {
            onExited: () => reset(),
          },
        }}
      >
        <DialogTitle onClose={onClose} loading={formState.isSubmitting}>
          คืนสถานะผู้เข้าสอบ
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ display: 'flex', flexDirection: 'column', gap: (theme) => theme.spacing(4) }}
        >
          <Flex
            sx={(theme) => ({
              flexDirection: 'column',
              border: `1px solid`,
              borderColor: theme.palette.divider,
              backgroundColor: theme.palette.grey[50],
              borderRadius: theme.shape.borderRadius,
              p: theme.spacing(4),
              gap: theme.spacing(4),
            })}
          >
            <Flex>
              <Flex sx={{ flexDirection: 'column', flex: 1, gap: (theme) => theme.spacing(1) }}>
                <Text variant='body2' color='text.secondary'>
                  ชื่อ-นามสกุล
                </Text>
                <Text sx={{ fontWeight: 'bold' }}>{fullName}</Text>
              </Flex>
              <Flex sx={{ flexDirection: 'column', flex: 1, gap: (theme) => theme.spacing(1) }}>
                <Text variant='body2' color='text.secondary'>
                  ID / Passport No.
                </Text>
                <Text sx={{ fontWeight: 'bold' }}>{idNumber}</Text>
              </Flex>
            </Flex>
            <Flex>
              <Flex sx={{ flexDirection: 'column', flex: 1, gap: (theme) => theme.spacing(1) }}>
                <Text variant='body2' color='text.secondary'>
                  หลักสูตรที่สมัคร
                </Text>
                <Text sx={{ fontWeight: 'bold' }}>{examCourseCode}</Text>
              </Flex>
              <Flex sx={{ flexDirection: 'column', flex: 1, gap: (theme) => theme.spacing(1) }}>
                <Text variant='body2' color='text.secondary'>
                  วัน-เวลาที่สอบ
                </Text>
                <Text sx={{ fontWeight: 'bold' }}>
                  {formatExamDateModal(examStartedAt, examEndedAt)}
                </Text>
              </Flex>
            </Flex>
          </Flex>

          <Text>
            คุณกำลังจะ{' '}
            <Text component='span' sx={{ fontWeight: 'bold' }}>
              &quot;คืนสถานะผู้เข้าสอบ&quot;
            </Text>{' '}
            กรุณาตรวจสอบความถูกต้องก่อนดำเนินการ
          </Text>

          <FormTextarea
            name='reason'
            label='กรุณาระบุเหตุผล'
            required
            fullWidth
            minRows={1}
            placeholder='เช่น ผู้เข้าสอบพร้อมสอบแล้ว, ยืนยันเข้าร่วมสอบ'
          />
        </DialogContent>
        <DialogActions>
          <Button
            size='large'
            variant='outlined'
            color='secondary'
            onClick={onClose}
            disabled={formState.isSubmitting || loading}
          >
            ยกเลิก
          </Button>
          <Button
            size='large'
            type='submit'
            color='primary'
            loading={formState.isSubmitting || loading}
          >
            คืนสถานะผู้เข้าสอบ
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
